import { Tab, Tabs } from '@material-ui/core';
import { FundraisingProps, renderHasChanged } from 'src/components/Pages/AddFundraisingPage';
import { Store, useAppDispatch } from 'src/redux/Store';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM } from 'src/constants/Form';
import { MouseEvent, useState } from 'react';
import { FundraisingLanguages } from 'src/constants/FundraisingLanguages';
import {
  CampaignMessage,
  Devices,
  FundraisingAssets,
  FundraisingModel,
  MainAsset,
  MainAssets,
  MediaAsset,
  SecondaryAssets,
  Step,
  TABS,
} from 'src/models/FundraisingModel';
import FundraiserTitle from 'src/components/UI/FundRaiserTitle';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import ChipLanguageSelector from 'src/components/Control/ChipLanguageSelector';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import Row from 'src/components/UI/Row';
import { openDialog } from 'src/redux/actions/dialogActions';
import { DIALOGS } from 'src/models/DialogModel';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import SubmenuLink from 'src/components/UI/SubmenuLink';
import Subtitle from 'src/components/UI/Subtitle';
import Button from '@material-ui/core/Button';
import MainFundraisingSlider from 'src/components/Control/MainFundraisingSlider';
import FundraisingSlider from 'src/components/Control/FundraisingSlider';
import { openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { getS3URL } from 'src/lib/S3File';
import FormRichTextEditor from 'src/components/Control/FormControls/FormRichTextEditor';
import Scroller from 'src/components/UI/Scroller';
import { denormalizeFundraisingSection } from 'src/lib/FundraisingHelper';
import { FundraisingSections } from 'src/constants/FundraisingTypes';
import BottomRow from 'src/components/UI/BottomRow';
import DeleteIcon from '@material-ui/icons/Delete';

const sectionList = [
  'main_slider',
  'matcher_slider',
  'product_slider',
  'billboard',
  'sponsor_slider',
  'secondary_slider',
  'campaign_text',
  'donor',
];

const sectionNames = {
  main_slider: 'Main Poster/Video Slider',
  matcher_slider: 'Matcher Image Slider',
  product_slider: 'Products Slider',
  billboard: 'Campaign Summary Billboard',
  sponsor_slider: 'Sponsor Image Slider',
  secondary_slider: 'Secondary Poster Slider',
  campaign_text: 'Campaign Text',
  donor: 'Donor and Team',
};

enum TAB {
  setting = 'setting',
  assets = 'assets',
}

const FundraisingAssetsForm = (props: FundraisingProps) => {
  const dispatch = useAppDispatch();
  const organisation = useSelector((state: Store) => state.currentOrganisation.id);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const {
    onChange,
    setErrors,
    fundraisingState,
    setHasChanged,
    hasChanged,
  } = props;

  const [assetsState, setAssetsState] = useState<FundraisingAssets>({
    sections: sectionList,
    main_slider: {},
    secondary_slider: {} as SecondaryAssets,
    campaign_message: {} as CampaignMessage,
    matcher_slider: [],
    sponsor_slider: [],
    ...(fundraisingState.assets as Partial<FundraisingAssets>),
  } as FundraisingAssets);

  const [language, setLanguage] = useState(FundraisingLanguages.en);

  const getLanguages = (): FundraisingLanguages[] => {
    if (!fundraisingState.assets || typeof fundraisingState.assets === 'string') {
      return [FundraisingLanguages.en];
    }
    const languageList = Object.getOwnPropertyNames(
      fundraisingState.assets.main_slider as Partial<FundraisingAssets>,
    );
    return languageList as FundraisingLanguages[];
  };

  const [languages, setLanguages] = useState(getLanguages);

  const [tab, setTab] = useState(TAB.setting);

  const fundraisingId = fundraisingState.fundraisingInfo?.id ?? 0;

  const onMessageChange = (value: string) => {
    setHasChanged(true);
    setAssetsState({
      ...assetsState,
      campaign_message: {
        ...assetsState.campaign_message,
        [language]: value,
      },
    });
  };

  const onValueChange = (value: any, field: string) => {
    setHasChanged(true);
    setAssetsState({
      ...assetsState,
      [field]: value,
    });
  };

  const onMainSliderChange = (device: Devices, assets: MediaAsset[]) => {
    const newLanguageAssets = {
      ...(assetsState.main_slider?.[language] ?? {}),
      [device]: assets,
    };

    const newAssetsState = {
      ...assetsState,
      main_slider: {
        ...assetsState.main_slider,
        [language]: newLanguageAssets,
      },
    };
    setAssetsState(newAssetsState);
    onChange(
      {
        ...fundraisingState,
        assets: newAssetsState,
      } as FundraisingModel,
      Step.assets,
      false,
    );
  };

  const onSliderChange = (field: string, assets: string[], hasLanguage?: boolean) => {
    const newAssetsState = { ...assetsState };
    if (hasLanguage) {
      newAssetsState[field][language] = assets;
      setAssetsState(newAssetsState);
      onChange(
        {
          ...fundraisingState,
          assets: newAssetsState,
        } as FundraisingModel,
        Step.assets,
        false,
      );
      return;
    }
    newAssetsState[field] = assets;
    setAssetsState(newAssetsState);
    onChange(
      {
        ...fundraisingState,
        assets: newAssetsState,
      } as FundraisingModel,
      Step.assets,
      false,
    );
  };

  const getFilePath = (device?: string) => {
    if (device) {
      return `${organisation}/events/fundraising-${fundraisingId}/${device}`;
    }
    return `${organisation}/events/fundraising-${fundraisingId}`;
  };

  const onSliderUploadImage = (field: string, image_url: string, hasLanguage?: boolean) => {
    const newAssetsState = { ...assetsState };
    if (hasLanguage) {
      if (!newAssetsState[field][language]) {
        newAssetsState[field][language] = [];
      }
      newAssetsState[field][language].push(`${getS3URL()}${image_url}`);
    } else {
      newAssetsState[field].push(`${getS3URL()}${image_url}`);
    }

    setAssetsState(newAssetsState);
    onChange(
      {
        ...fundraisingState,
        assets: newAssetsState,
      } as FundraisingModel,
      Step.assets,
      false,
    );
  };

  const openUploadImage = (field: string, width: number, height: number, hasLanguage?: boolean) => {
    dispatch(openModal({
      modal: MODALS.uploadImage,
      payload: {
        path: getFilePath(),
        size: {
          width,
          height,
          ratio: width / height,
        },
        name: null,
        onUpload: (image_url: string) => onSliderUploadImage(field, image_url, hasLanguage),
      },
    }));
  };

  const onMainUploadImage = (device: Devices, image_url: string) => {
    assetsState.main_slider[language] ??= {
      mobile: [] as MediaAsset[],
      desktop: [] as MediaAsset[],
    } as MainAsset;
    const deviceAssets = assetsState.main_slider[language]![device] ?? [];
    deviceAssets.push({ image_url: `${getS3URL()}${image_url}`, video_url: '' });
    onMainSliderChange(device, deviceAssets);
  };

  const openMainUploadImage = (device: Devices, width: number, height: number) => {
    dispatch(openModal({
      modal: MODALS.uploadImage,
      payload: {
        path: getFilePath(device),
        size: {
          width,
          height,
          ratio: width / height,
        },
        name: null,
        onUpload: (image_url: string) => onMainUploadImage(device, image_url),
      },
    }));
  };

  const onAddLanguageChange = (value: FundraisingLanguages) => {
    /* eslint-disable @typescript-eslint/naming-convention */
    const main_slider = assetsState.main_slider ?? {} as MainAssets;
    const secondary_slider = assetsState.secondary_slider ?? {} as SecondaryAssets;
    const campaign_message = assetsState.campaign_message ?? {} as CampaignMessage;
    /* eslint-enable @typescript-eslint/naming-convention */

    main_slider[value] = {} as MainAsset;
    secondary_slider[value] = [] as string[];
    campaign_message[value] = '';

    setAssetsState({
      ...assetsState,
      main_slider,
      secondary_slider,
      campaign_message,
    });
    setLanguages((prev) => [...prev, value]);
    setLanguage(value);
    onChange(
      {
        ...fundraisingState,
        assets: assetsState,
      } as FundraisingModel,
      Step.assets,
      false,
    );
  };

  const onDeleteLanguage = (value: FundraisingLanguages) => {
    /* eslint-disable @typescript-eslint/naming-convention */
    const main_slider = assetsState.main_slider ?? {} as MainAssets;
    const secondary_slider = assetsState.secondary_slider ?? {} as SecondaryAssets;
    const campaign_message = assetsState.campaign_message ?? {} as CampaignMessage;
    /* eslint-enable @typescript-eslint/naming-convention */

    main_slider[value] = undefined;
    secondary_slider[value] = undefined;
    campaign_message[value] = '';

    setAssetsState({
      ...assetsState,
      main_slider,
      secondary_slider,
      campaign_message,
    });

    const index = languages.indexOf(value);
    languages.splice(index, 1);
    setLanguages(languages);
    setLanguage(languages[0]);
    onChange(
      {
        ...fundraisingState,
        assets: assetsState,
      } as FundraisingModel,
      Step.assets,
      false,
    );
  };

  const onColorChange = (value: any) => {
    setHasChanged(true);
    setAssetsState({
      ...assetsState,
      theme_color: value.hex,
    });
  };

  const onDragEnd = (dropResult: any) => {
    const {
      destination,
      source,
    } = dropResult;
    if (!destination) return;

    const newSections = [...assetsState.sections];
    const [removed] = newSections.splice(source.index, 1);
    newSections.splice(destination.index, 0, removed);

    onValueChange(newSections, 'sections');
  };

  const openColorPicker = () => {
    dispatch(openDialog({
      dialog: DIALOGS.colorPicker,
      payload: { onChange: onColorChange, color: assetsState.theme_color },
    }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>, advance: boolean) => {
    event.preventDefault();
    setLanguages([FundraisingLanguages.en]);
    dispatch(setError({ [FORM.fundraising]: {} }));
    setErrors(TABS.assets, false);
    setHasChanged(false);
    onChange(
      {
        ...fundraisingState,
        assets: denormalizeFundraisingSection(FundraisingSections.assets, assetsState),
      } as FundraisingModel,
      Step.assets,
      advance,
    );
  };

  const renderTabs = () => languages.map((current) => (
    <Tab
      label={(
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{t(lang, `forms.fundraising.${current}`)}</span>
          <span
            onClick={(e) => {
              e.stopPropagation(); // Prevent tab selection on icon click
              dispatch(
                openDialog(
                  {
                    dialog: DIALOGS.confirmRemoveLanguageDialog,
                    payload: () => onDeleteLanguage(current),
                  },
                ),
              );
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <DeleteIcon style={{ fontSize: '18px' }} />
          </span>
        </span>
      )}
      value={current} />
  ));

  const ListStyle = {
    listStyleType: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0px',
    width: '518px',
  };

  const ItemStyle = {
    border: '1px solid #c8c8c8',
    borderRadius: '8px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    width: '100%',
    backgroundColor: 'white',
  };

  const editorValue = assetsState?.campaign_message[language]?.trim() || '';
  return (
    <>
      <FundraiserTitle>Assets Informations</FundraiserTitle>
      <Row>
        <SubmenuLink
          color="rgb(165, 23, 96)"
          onClick={() => setTab(TAB.setting)}
          selected={tab === TAB.setting}>
          Settings
        </SubmenuLink>
        <SubmenuLink
          color="rgb(165, 23, 96)"
          onClick={() => setTab(TAB.assets)}
          selected={tab === TAB.assets}>
          Assets
        </SubmenuLink>
      </Row>
      <div style={{ marginBottom: '12px' }}>
        <Tabs
          value={language}
          onChange={(e, value) => setLanguage(value)}
          style={{ width: 'auto', display: 'inline-flex' }}>
          {renderTabs()}
        </Tabs>
        <ChipLanguageSelector values={languages} onChange={onAddLanguageChange} />
      </div>
      { tab === TAB.setting && (
      <>
        <Row alignItems="start">
          <div style={{ width: '50%' }}>
            <Subtitle style={{ marginTop: '0px' }}>Theme color(background and buttons)</Subtitle>
            <Row alignItems="center">
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  display: 'inline-block',
                  borderRadius: '5px',
                  border: '1px solid black',
                  backgroundColor: assetsState.theme_color,
                  cursor: 'pointer',
                }}
                onClick={() => openColorPicker()}>&nbsp;
              </div>
              <span style={{
                fontSize: '1rem',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.00938em',
              }}>
                {assetsState.theme_color}
              </span>
            </Row>
          </div>
          <div style={{ width: '50%' }}>
            <Subtitle style={{ marginTop: '0px' }}>Show/Hide sections</Subtitle>
            <Row>
              <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                <FormCheckbox
                  label="Show Main Slider"
                  name="show_main_slider"
                  onChange={onValueChange}
                  checked={!!assetsState.show_main_slider} />
                <FormCheckbox
                  label="Show Matcher Slider"
                  name="show_matcher_slider"
                  onChange={onValueChange}
                  checked={!!assetsState.show_matcher_slider} />
              </div>
              <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                <FormCheckbox
                  label="Show Secondary Slider"
                  name="show_secondary_slider"
                  onChange={onValueChange}
                  checked={!!assetsState.show_secondary_slider} />
                <FormCheckbox
                  label="Show Sponsor Slider"
                  name="show_sponsor_slider"
                  onChange={onValueChange}
                  checked={!!assetsState.show_sponsor_slider} />
              </div>
            </Row>
          </div>
        </Row>
        <Row alignItems="normal">
          <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
            <Subtitle style={{ marginTop: '0px' }}>Sections order</Subtitle>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {
              (provided: any) => (
                <ul
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={ListStyle}>
                  {assetsState.sections.map((section: string, index: number) => (
                    <Draggable
                      key={`product-${section}`}
                      draggableId={section}
                      index={index}>
                      {(providedDraggable: any) => (
                        <li
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          {...providedDraggable.dragHandleProps}
                          style={{
                            ...ItemStyle,
                            ...providedDraggable.draggableProps.style, // <-- merge!
                          }}>
                          <span style={{ width: '100%' }}>{sectionNames[section]}</span>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )
            }
              </Droppable>
            </DragDropContext>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', width: '50%',
          }}>
            <Subtitle style={{ marginTop: '0px', marginBottom: '14px' }}>Campaign Message</Subtitle>
            <FormRichTextEditor
              height="238px"
              label="Campaign Message"
              form={FORM.fundraising}
              name="campaign_message"
              onChange={onMessageChange}
              value={editorValue}
              toolbar="invitation" />
          </div>
        </Row>
      </>
      )}
      { tab === TAB.assets && (
      <Scroller height="580px">
        <Subtitle style={{ marginTop: '8px' }}>
          Main Poster/Videos
        </Subtitle>
        <Row style={{ marginTop: '16px', marginBottom: '16px' }}>
          <span style={{ width: '70px' }}>DESKTOP</span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { openMainUploadImage('desktop', 2547, 1184); }}
            key="main-upload-image-desktop"
            size="small">
            Upload Image
          </Button>
          <i>(2547 x 1184px | Max size 1.5Mb | jpg or png)</i>
        </Row>
        <MainFundraisingSlider
          onChange={(assets: MediaAsset[]) => { onMainSliderChange('desktop', assets); }}
          assetList={assetsState.main_slider[language]?.desktop ?? []}
          ratio={2547 / 1184} />
        <Row style={{ marginTop: '16px', marginBottom: '16px' }}>
          <span style={{ width: '70px' }}>MOBILE</span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { openMainUploadImage('mobile', 724, 724); }}
            key="main-upload-image-mobile"
            size="small">
            Upload Image
          </Button>
          <i>(724px x 724px | Max size 1Mb | jpg or png)</i>
        </Row>
        <MainFundraisingSlider
          onChange={(assets: MediaAsset[]) => { onMainSliderChange('mobile', assets); }}
          assetList={assetsState.main_slider[language]?.mobile ?? []}
          ratio={1} />
        <Subtitle style={{ marginTop: '16px' }}>
          Secondary Poster/Videos
        </Subtitle>
        <Row style={{ marginTop: '16px', marginBottom: '16px' }}>
          <span>DESKTOP & MOBILE</span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { openUploadImage('secondary_slider', 874, 628, true); }}
            key="secondary-upload-image"
            size="small">
            Upload Image
          </Button>
          <i>(874px x 628px | Max size 1.5Mb | jpg or png)</i>
        </Row>
        <FundraisingSlider
          onChange={(assets: string[]) => { onSliderChange('secondary_slider', assets, true); }}
          assetList={assetsState.secondary_slider[language] ?? []}
          ratio={874 / 628} />
        <Subtitle style={{ marginTop: '16px' }}>
          Matchers
        </Subtitle>
        <Row style={{ marginTop: '16px', marginBottom: '16px' }}>
          <span>DESKTOP & MOBILE</span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { openUploadImage('matcher_slider', 724, 724, false); }}
            key="matcher-upload-image-desktop"
            size="small">
            Upload Image
          </Button>
          <i>(724px x 724px | Max size 150Kb | jpg or png)</i>
        </Row>
        <FundraisingSlider
          onChange={(assets: string[]) => { onSliderChange('matcher_slider', assets, false); }}
          assetList={assetsState.matcher_slider ?? []}
          ratio={724 / 724} />
        <Subtitle style={{ marginTop: '16px' }}>
          Sponsors
        </Subtitle>
        <Row style={{ marginTop: '16px', marginBottom: '16px' }}>
          <span>DESKTOP & MOBILE</span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { openUploadImage('sponsor_slider', 724, 724, false); }}
            key="sponsor-upload-image"
            size="small">
            Upload Image
          </Button>
          <i>(724px x 724px | Max size 150Kb | jpg or png)</i>
        </Row>
        <FundraisingSlider
          onChange={(assets: string[]) => { onSliderChange('sponsor_slider', assets, false); }}
          assetList={assetsState.sponsor_slider ?? []}
          ratio={724 / 724} />
      </Scroller>
      )}
      <BottomRow>
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => handleSubmit(e, false)}
          key="submit-button">
          Save{renderHasChanged(!hasChanged)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => handleSubmit(e, true)}
          key="submit-next-button">
          Save{renderHasChanged(!hasChanged)} and Next Step
        </Button>
      </BottomRow>
    </>
  );
};

export default FundraisingAssetsForm;
